import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import useStyles from "./styles";
import { usePoiSources, usePoiStats, usePoiTerritories } from "../../api/poi";

const TIER_META: Record<string, { color: "success" | "primary" | "default"; help: string }> = {
  full: {
    color: "success",
    help:
      "Toutes les sources : registre national, scrapers contrôle technique, open data, OSM, Google. Coûteux à construire et à exploiter — réservé aux pays où vous avez des flottes clientes.",
  },
  standard: {
    color: "primary",
    help:
      "Sources paneuropéennes uniquement : OSM + point d'accès national AFIR + Google à la demande. Coût marginal quasi nul. C'est le niveau pour les pays que vos flottes traversent.",
  },
  default: {
    color: "default",
    help: "OSM uniquement, chargé à la demande. Ne coûte rien tant que personne ne le demande.",
  },
};

const KIND_LABELS: Record<string, string> = {
  repair_centre: "Garages",
  inspection: "Contrôle technique",
  fuel_station: "Stations-service",
  ev_charging: "Bornes de recharge",
  tyre_centre: "Pneumatiques",
  glass_centre: "Pare-brise",
  body_shop: "Carrosserie",
  dealer: "Concessionnaires",
  car_wash: "Lavage",
  unknown: "Non classé",
};

export const CoverageTab: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { countries, isLoading } = usePoiTerritories();
  const { stats } = usePoiStats();
  const { sources } = usePoiSources();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const maxCount = Math.max(1, ...countries.map((c) => c.sign_count));
  const kindEntries = Object.entries(stats?.by_kind ?? {}).sort((a, b) => b[1] - a[1]);

  /** Which sources apply to a country: its own plus every EU-wide one. */
  const sourcesFor = (code: string) =>
    sources.filter(
      (s) => s.enabled && (s.is_eu_wide || s.countries.includes(code))
    );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          "poi.coverageIntro",
          "L'architecture est paneuropéenne ; le portefeuille de sources est étagé par pays. La couverture suit vos flottes, la profondeur suit votre chiffre d'affaires."
        )}
      </Typography>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        {/* ── Countries ────────────────────────────────────────────────── */}
        <Card className={classes.card} sx={{ flex: "2 1 420px", minWidth: 0 }}>
          <CardContent className={classes.cardContent}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
              {t("poi.byCountry", "Couverture par pays")}
            </Typography>

            <TableContainer component={Paper} className={classes.tableWrap}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("poi.country", "Pays")}</TableCell>
                    <TableCell>{t("poi.tier", "Niveau")}</TableCell>
                    <TableCell align="right">{t("poi.entities", "Fiches")}</TableCell>
                    <TableCell width="30%">{t("poi.share", "Part")}</TableCell>
                    <TableCell align="right">{t("poi.activeSources", "Sources")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {countries.map((c) => {
                    const meta = TIER_META[c.tier] ?? TIER_META.default;

                    return (
                      <TableRow key={c.code} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {c.code}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.name}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Tooltip title={meta.help}>
                            <Chip
                              size="small"
                              label={c.tier}
                              color={meta.color}
                              variant="outlined"
                            />
                          </Tooltip>
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {c.sign_count.toLocaleString("fr-FR")}
                        </TableCell>

                        <TableCell>
                          <LinearProgress
                            variant="determinate"
                            value={(c.sign_count / maxCount) * 100}
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </TableCell>

                        <TableCell align="right">
                          <Chip
                            size="small"
                            variant="outlined"
                            label={sourcesFor(c.code).length}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* ── Kinds ────────────────────────────────────────────────────── */}
        <Card className={classes.card} sx={{ flex: "1 1 280px", minWidth: 0 }}>
          <CardContent className={classes.cardContent}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
              {t("poi.byKind", "Répartition par type")}
            </Typography>

            {kindEntries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("poi.noData", "Aucune donnée pour l'instant.")}
              </Typography>
            ) : (
              kindEntries.map(([kind, count]) => (
                <Box
                  key={kind}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 0.75,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2">{KIND_LABELS[kind] ?? kind}</Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {count.toLocaleString("fr-FR")}
                  </Typography>
                </Box>
              ))
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
